import { GOODS, MAX_REGIONS, MAX_REPLAY_COMMANDS, MAX_REPLAY_TICKS, MILESTONES } from "./constants";
import { REPLAY_VERSION, replayWorld } from "./replay";
import type { ReplayCommand } from "./replay";
import { worldRect } from "./world";
import type { Corridor, GameNode, GameState, GoodId, Route, Truck } from "./types";

/**
 * Save format. Pure by design: no localStorage, no clock, no crypto — the
 * impure side lives in src/ui/storage.ts and passes timestamps and ids in.
 */

export const SAVE_VERSION = 12;

/**
 * A world from before RFC-0005 has no seed. 0 is a valid stream; it is simply
 * not the stream that map came from — nothing regenerates it, and the world
 * grows from `LEGACY_SEED + stage` from here on.
 */
export const LEGACY_SEED = 0;

interface SlotBase {
  /** Stable for the life of the slot. Generated by the caller, never reused. */
  readonly id: string;
  /** Player-editable. Defaults to a generated label, never empty. */
  name: string;
  /** Epoch milliseconds, stamped by the caller. Orders the menu. */
  updatedAt: number;
}

export interface SnapshotSlot extends SlotBase {
  readonly kind: "snapshot";
  world: GameState;
}

export interface ReplaySlot extends SlotBase {
  readonly kind: "replay";
  readonly replayVersion: number;
  readonly seed: number;
  readonly tick: number;
  readonly commands: readonly ReplayCommand[];
  /** Reconstructed on parse; serialize() deliberately omits it. */
  world: GameState;
}

export type Slot = SnapshotSlot | ReplaySlot;

export interface SaveFile {
  readonly version: number;
  /** Epoch milliseconds, stamped by the caller. Decides the winner if a remote
   *  copy is ever compared against a local one. */
  readonly updatedAt: number;
  /** Stable across devices, generated once. An identity without an account. */
  readonly profileId: string;
  /** Id of the slot the game resumes into, or null if the list is empty. */
  readonly activeSlot: string | null;
  readonly slots: Slot[];
}

export function emptySave(profileId: string, updatedAt: number): SaveFile {
  return {
    version: SAVE_VERSION,
    updatedAt,
    profileId,
    activeSlot: null,
    slots: [],
  };
}

/** The active slot, or null. Saves every call site the same find(). */
export function activeSlotOf(save: SaveFile): Slot | null {
  return save.slots.find((s) => s.id === save.activeSlot) ?? null;
}

export function serialize(save: SaveFile): string {
  return JSON.stringify(save, function omitReplayWorld(key, value) {
    return key === "world" && this.kind === "replay" ? undefined : value;
  });
}

// ─── Validation ─────────────────────────────────────────────────────────────

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const isStr = (v: unknown): v is string => typeof v === "string";

const TRUCK_STATES = ["loading", "outbound", "unloading", "returning"];

const MILESTONE_IDS: readonly string[] = MILESTONES.map((m) => m.id);

/**
 * Milestones decide whether a map extension still fires, so an unknown id would
 * silently freeze a world's map at its current size — a save that keeps playing
 * and quietly stops growing is worse than one that visibly did not load.
 */
function validMilestones(v: unknown): boolean {
  if (!Array.isArray(v)) return false;
  if (!v.every((x) => isStr(x) && MILESTONE_IDS.includes(x))) return false;
  return new Set(v).size === v.length;
}

/** Guards every field that is later read as a number, so NaN never reaches money. */
const allNum = (v: Record<string, unknown>, keys: string[]): boolean =>
  keys.every((k) => isNum(v[k]));

const isGood = (v: unknown): boolean => isStr(v) && Object.hasOwn(GOODS, v);

/** Every good present and finite. A missing key becomes NaN in money one hop later. */
const validStock = (v: unknown): boolean =>
  isRecord(v) && (Object.keys(GOODS) as GoodId[]).every((g) => isNum(v[g]));

function validNode(v: unknown): v is GameNode {
  if (!isRecord(v)) return false;
  if (!allNum(v, ["id", "x", "y"]) || !isStr(v["name"])) return false;
  if (v["kind"] === "producer") {
    // `level` prices the next upgrade (RFC-0017 §3.4). Checked as a number like
    // the rest: a missing one would make producerUpgradeCost() return NaN and
    // the purchase would be silently free.
    return isGood(v["good"]) && allNum(v, ["rate", "cap", "stock", "level"]);
  }
  if (v["kind"] === "city") {
    // Three full records since RFC-0016, checked exactly like a hub's stock and
    // for the same reason: a city missing one good pays NaN for it on the first
    // delivery, and the loss is silent until the balance goes to NaN with it.
    return isNum(v["trust"])
      && ["demand", "demandMax", "backlog"].every((k) => validStock(v[k]));
  }
  if (v["kind"] === "transfer") {
    // `refines` is checked, not defaulted: a hub that does not say what it is
    // would read as a buffer in sim.ts and as a refinery in the panel, and the
    // player would only find out by watching a store that never converts.
    return isNum(v["cap"]) && validStock(v["stock"]) && typeof v["refines"] === "boolean";
  }
  return false;
}

/**
 * `lanes` is checked as an integer ≥ 1, not merely as a finite number. A
 * hand-edited 0 or 2.5 pins congestionFactor() at CONGESTION_MIN_SPEED forever,
 * which reads as a bug in the renderer rather than as a broken save — the same
 * argument validMilestones() makes about an unknown milestone id.
 */
function validRoute(v: unknown): v is Route {
  return isRecord(v) && allNum(v, ["id", "from", "to"]) && isGood(v["good"])
    && Array.isArray(v["corridors"]) && v["corridors"].length > 0
    && v["corridors"].every((c: unknown) => isNum(c));
}

/**
 * Length and lanes are checked here now, for the reason the old comment on
 * validRoute gave: a hand-edited 0 or 2.5 pins congestionFactor() at
 * CONGESTION_MIN_SPEED forever, which reads as a renderer bug rather than as a
 * broken save.
 */
function validCorridor(v: unknown): v is Corridor {
  return isRecord(v) && allNum(v, ["id", "from", "to", "length"])
    && (v["length"] as number) > 0
    && Number.isInteger(v["lanes"]) && (v["lanes"] as number) >= 1;
}

function validTruck(v: unknown): v is Truck {
  return (
    isRecord(v) &&
    allNum(v, ["id", "route", "pos", "cargo", "timer", "idle"]) &&
    isStr(v["state"]) &&
    TRUCK_STATES.includes(v["state"])
  );
}

/**
 * Walks into the arrays, not just past them. Every reference a route carries is
 * resolved here, because findNode() and producerOf() throw on a miss and the
 * frame loop has no way to recover a world that is already on screen.
 *
 * ponytail: a truck whose route was deleted still passes — sim.ts skips it
 * cleanly, so there is nothing to protect against.
 */
function validWorld(v: unknown): v is GameState {
  if (!isRecord(v)) return false;
  if (!allNum(v, [
    "seed", "money", "day", "dayTime", "earned", "nextRouteId", "nextTruckId",
    "nextCorridorId",
  ])) {
    return false;
  }
  if (!Number.isInteger(v["regionCount"]) || (v["regionCount"] as number) < 1 || (v["regionCount"] as number) > MAX_REGIONS) return false;
  if (!validMilestones(v["milestones"])) return false;

  const { nodes, routes, trucks, corridors } = v as Record<string, unknown>;
  if (!Array.isArray(nodes) || !Array.isArray(routes) || !Array.isArray(trucks)) return false;
  if (!Array.isArray(corridors)) return false;
  if (
    !nodes.every(validNode) || !routes.every(validRoute) || !trucks.every(validTruck)
    || !corridors.every(validCorridor)
  ) {
    return false;
  }

  const state = v as unknown as GameState;
  const bounds = worldRect(state);
  if (!(nodes as GameNode[]).every((n) => n.x >= bounds.x && n.x <= bounds.x + bounds.width && n.y >= bounds.y && n.y <= bounds.y + bounds.height)) return false;
  const byId = new Map<number, GameNode>(nodes.map((n: GameNode) => [n.id, n]));
  // Every corridor a route names has to exist: routeLength() throws on a miss,
  // and it runs inside the frame loop where nothing can recover (RFC-0033 §3.3).
  const corridorIds = new Set((corridors as Corridor[]).map((c) => c.id));
  return routes.every((r: Route) => {
    const from = byId.get(r.from);
    const to = byId.get(r.to);
    if (!from || !to) return false;
    if (from.kind === "city" || to.kind === "producer") return false;
    if (!r.corridors.every((id) => corridorIds.has(id))) return false;
    // A route claiming a good its producer does not make would pay Erz prices
    // for Holz: producer stock is untyped, only the route names the good.
    return from.kind !== "producer" || from.good === r.good;
  });
}

function validSlot(v: unknown): v is Slot {
  if (!isRecord(v) || !isStr(v["id"]) || !isStr(v["name"]) || !isNum(v["updatedAt"])) return false;
  if (v["kind"] === "snapshot") return validWorld(v["world"]);
  if (
    v["kind"] !== "replay" || v["replayVersion"] !== REPLAY_VERSION
    || !isNum(v["seed"]) || !Number.isInteger(v["tick"]) || (v["tick"] as number) < -1
    || (v["tick"] as number) > MAX_REPLAY_TICKS
    || !Array.isArray(v["commands"]) || v["commands"].length > MAX_REPLAY_COMMANDS
    || !v["commands"].every(validReplayCommand)
  ) return false;
  const commands = v["commands"] as ReplayCommand[];
  // One past the stored tick, not level with it: the recorder stamps an action
  // with the tick it will precede, and a paused game runs no ticks — so a road
  // built while paused is legitimately one ahead of everything that has run.
  if (commands.some((command, index) => command.tick > (v["tick"] as number) + 1
    || (index > 0 && command.tick < commands[index - 1]!.tick))) return false;
  try {
    const world = replayWorld(v["seed"] as number, commands, v["tick"] as number);
    Object.defineProperty(v, "world", { value: world, enumerable: false });
    return true;
  } catch {
    return false;
  }
}

function validReplayCommand(v: unknown): v is ReplayCommand {
  if (!isRecord(v) || !Number.isInteger(v["tick"]) || (v["tick"] as number) < 0) return false;
  const id = (key: string): boolean => Number.isInteger(v[key]) && (v[key] as number) >= 0;
  switch (v["type"]) {
    case "buildRoute": return id("from") && id("to") && (v["good"] === undefined || isGood(v["good"]));
    case "buildRouteVia": return id("from") && id("to") && id("via") && (v["good"] === undefined || isGood(v["good"]));
    case "buyTruck": case "sellTruck": case "sellRoute": return id("routeId");
    case "buyLane": return id("corridorId");
    case "upgradeProducer": return id("nodeId");
    case "buildProducer": return v["good"] === "holz" || v["good"] === "erz";
    case "expandRegion": return true;
    default: return false;
  }
}

/** Everything every version of the file has in common. */
function validEnvelope(v: Record<string, unknown>): boolean {
  return isNum(v["updatedAt"]) && isStr(v["profileId"]);
}

// ─── Migration ──────────────────────────────────────────────────────────────

/**
 * Each step transforms an untrusted record into the next version's shape and
 * validates nothing. The chain's result is checked once, as a version 3 save,
 * by parseSave — see RFC-0002 §3.5.
 *
 * The order matters: validWorld() demands the fields this migration adds, so a
 * step that validated its input would reject every save it exists to rescue.
 * A migration therefore reads only the fields it moves, through the same
 * guards as everything else.
 */

/**
 * The shape RFC-0001 wrote, into one slot. The id comes from the caller because
 * a v1 save has none and this file may not reach for crypto.
 */
function migrateV1(
  v: Record<string, unknown>, slotId: string, name: string,
): Record<string, unknown> {
  const world = v["world"];
  const updatedAt = v["updatedAt"];
  const empty = world === null || world === undefined;
  // Rebuilt field by field rather than spread: `world` and `profile` must not
  // survive into the new shape, and neither must anything a hand-edited file
  // added on the side.
  return {
    version: 2,
    updatedAt,
    profileId: v["profileId"],
    activeSlot: empty ? null : slotId,
    slots: empty ? [] : [{ id: slotId, name, updatedAt, world }],
  };
}

/** Drops the end state a world could be in, and starts its revenue at zero. */
function migrateWorldV2(v: unknown): unknown {
  if (!isRecord(v)) return v;
  const world: Record<string, unknown> = { ...v, earned: 0, milestones: [] };
  delete world["outcome"];
  return world;
}

/**
 * `earned: 0` is a deliberate lie of omission and the honest option: a v2 world
 * has earned a six-figure sum that is recorded nowhere, and reconstructing it
 * from money plus sunk costs would ignore every euro of upkeep already paid.
 * The migrated world re-earns its milestones over two or three days of play.
 */
function migrateV2(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    version: 3,
    updatedAt: v["updatedAt"],
    // profileId stays — a device identity costs one string. `profile` goes: both
    // of its fields counted finished worlds, and worlds do not finish any more.
    profileId: v["profileId"],
    activeSlot: v["activeSlot"] ?? null,
    slots: slots.map((s: unknown) =>
      isRecord(s) ? { ...s, world: migrateWorldV2(s["world"]) } : s,
    ),
  };
}

/**
 * Stamps the legacy seed into every world. The map itself is left exactly as it
 * was: it is stored, it is what the player has been playing, and regenerating
 * it from a seed it never came from would hand them a different world.
 */
function migrateV3(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    ...v,
    version: 4,
    slots: slots.map((s: unknown) =>
      isRecord(s) && isRecord(s["world"])
        ? { ...s, world: { seed: LEGACY_SEED, ...s["world"] } }
        : s,
    ),
  };
}

/**
 * Every road built before RFC-0014 is single-lane. Written rather than defaulted
 * at the read site: an optional `lanes` would spread `?? 1` across haul.ts,
 * sim.ts, panel.ts and canvas.ts and would leave the type claiming forever that
 * the field might be missing.
 */
function migrateV4(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    ...v,
    version: 5,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const routes = Array.isArray(world["routes"]) ? world["routes"] : [];
      return {
        ...s,
        world: {
          ...world,
          routes: routes.map((r: unknown) => (isRecord(r) ? { lanes: 1, ...r } : r)),
        },
      };
    }),
  };
}

/**
 * One scalar appetite becomes one record per good (RFC-0016).
 *
 * The whole amount goes on the good the city's **inbound routes** carry, and is
 * split evenly across every good when it has no route. Any other choice invents
 * an appetite the player never had — and this one keeps every existing route
 * profitable across the migration, which is the property that matters on the day
 * somebody loads an old save: the road that paid full price yesterday still does.
 *
 * A city fed two goods through two roads splits between exactly those two, for
 * the same reason.
 */
function migrateV5(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  const goods = Object.keys(GOODS) as GoodId[];
  return {
    ...v,
    version: 6,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const routes = Array.isArray(world["routes"]) ? world["routes"] : [];
      const nodes = Array.isArray(world["nodes"]) ? world["nodes"] : [];
      return {
        ...s,
        world: {
          ...world,
          nodes: nodes.map((n: unknown) => {
            if (!isRecord(n) || n["kind"] !== "city") return n;
            // Only a scalar appetite is this migration's business. A city that
            // already carries records is left alone: a hand-edited file, or a
            // world that came up through the chain from an older version whose
            // fixture was written in today's shape.
            if (!isNum(n["demandMax"])) return n;
            const inbound = [...new Set(
              routes
                .filter((r: unknown) => isRecord(r) && r["to"] === n["id"])
                .map((r: Record<string, unknown>) => r["good"])
                .filter((g: unknown): g is GoodId => isGood(g)),
            )];
            const onto = inbound.length > 0 ? inbound : goods;
            const spread = (key: string): Record<string, number> => {
              const raw = n[key];
              const each = (isNum(raw) ? raw : 0) / onto.length;
              return Object.fromEntries(
                goods.map((g) => [g, onto.includes(g) ? each : 0]),
              );
            };
            return {
              ...n,
              demand: spread("demand"),
              demandMax: spread("demandMax"),
              backlog: spread("backlog"),
            };
          }),
        },
      };
    }),
  };
}

/**
 * Repairs the appetite migrateV5() invented for a city with no route.
 *
 * That step split the old scalar evenly across **every** good when a city had no
 * inbound road to learn from. It was the honest reading of "invent nothing", and
 * in a real save it turned out to invent something worse: the generator never
 * builds a two-good city — `TWO_GOOD_CITIES` is 0, and the sweep behind that
 * number is in RFC-0016 §3.6 — so a migrated world ended up with a kind of city
 * the game does not otherwise contain. `BACKLOG_CAP` is per good, so each of them
 * reaches `TRUST_MIN` about twice as fast, and a city that had no road was
 * already the one least likely to be served.
 *
 * Found in a day-251 world with five unserved cities, all of them sitting at
 * exactly `TRUST_MIN`.
 *
 * The repair uses the rule the generator itself uses (RFC-0016 §3.4): a city
 * wants the good of its **nearest producer**, and the whole amount goes there.
 * Only cities with no inbound route are touched, because that is the exact
 * signature of the even split — a two-good city that a later `TWO_GOOD_CITIES`
 * hands out deliberately must survive this untouched.
 */
function migrateV6(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  const goods = Object.keys(GOODS) as GoodId[];
  return {
    ...v,
    version: 7,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const routes = Array.isArray(world["routes"]) ? world["routes"] : [];
      const nodes = Array.isArray(world["nodes"]) ? world["nodes"] : [];
      const producers = nodes.filter((n: unknown): n is Record<string, unknown> =>
        isRecord(n) && n["kind"] === "producer" && isGood(n["good"])
        && isNum(n["x"]) && isNum(n["y"]));

      return {
        ...s,
        world: {
          ...world,
          nodes: nodes.map((n: unknown) => {
            if (!isRecord(n) || n["kind"] !== "city") return n;
            if (routes.some((r: unknown) => isRecord(r) && r["to"] === n["id"])) return n;
            const max = n["demandMax"];
            if (!isRecord(max)) return n;
            const wanted = goods.filter((g) => isNum(max[g]) && (max[g] as number) > 0);
            if (wanted.length < 2) return n;

            // Nearest producer of any good — the same question §3.4 asks, minus
            // the repair pass, because here there is exactly one city to place.
            let keep = wanted[0]!;
            let best = Infinity;
            for (const p of producers) {
              const good = p["good"] as GoodId;
              if (!wanted.includes(good)) continue;
              const dx = (p["x"] as number) - (n["x"] as number);
              const dy = (p["y"] as number) - (n["y"] as number);
              const d = Math.hypot(dx, dy);
              if (d < best) { best = d; keep = good; }
            }

            const collapse = (key: string): Record<string, number> => {
              const rec = n[key];
              const total = isRecord(rec)
                ? goods.reduce((sum, g) => sum + (isNum(rec[g]) ? rec[g] : 0), 0)
                : 0;
              return Object.fromEntries(goods.map((g) => [g, g === keep ? total : 0]));
            };
            // The backlog collapses too. It was split by the same step, and
            // leaving it spread would keep the debt this repair exists to end.
            return {
              ...n,
              demand: collapse("demand"),
              demandMax: collapse("demandMax"),
              backlog: collapse("backlog"),
            };
          }),
        },
      };
    }),
  };
}

/**
 * Every producer a generator made starts at level 0 (RFC-0017 §3.4).
 *
 * Written rather than defaulted at the read site, for the same reason `lanes`
 * was in migrateV4: an optional field spreads `?? 0` across sim.ts and panel.ts
 * and leaves the type claiming forever that it might be missing.
 */
function migrateV7(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    ...v,
    // The successor, spelled out. SAVE_VERSION here would hand a v7 file
    // straight to the validator and skip every migration between — which is
    // exactly what it did: a v7 world never got the werkzeug keys migrateV8()
    // adds, so validStock() rejected the whole file and the save was lost.
    version: 8,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const nodes = Array.isArray(world["nodes"]) ? world["nodes"] : [];
      return {
        ...s,
        world: {
          ...world,
          nodes: nodes.map((n: unknown) =>
            isRecord(n) && n["kind"] === "producer" ? { level: 0, ...n } : n),
        },
      };
    }),
  };
}

/**
 * Werkzeug exists (RFC-0015), so every full per-good record needs the key —
 * a hub's stock and all three of a city's, because validStock() walks
 * `Object.keys(GOODS)` and a missing key reaches money as NaN one hop later.
 *
 * `refines` rides along here rather than defaulting at the read site, the same
 * argument migrateV7() makes about `level`. Every hub that already exists stays
 * a buffer: making one convert would take away storage the player was using.
 */
function migrateV8(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  // Written before the spread, so a record that already has the key keeps it.
  const withWerkzeug = (rec: unknown): unknown =>
    isRecord(rec) ? { werkzeug: 0, ...rec } : rec;
  return {
    ...v,
    version: 9,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const nodes = Array.isArray(world["nodes"]) ? world["nodes"] : [];
      return {
        ...s,
        world: {
          ...world,
          nodes: nodes.map((n: unknown) => {
            if (!isRecord(n)) return n;
            if (n["kind"] === "transfer") {
              return { refines: false, ...n, stock: withWerkzeug(n["stock"]) };
            }
            if (n["kind"] === "city") {
              return {
                ...n,
                demand: withWerkzeug(n["demand"]),
                demandMax: withWerkzeug(n["demandMax"]),
                backlog: withWerkzeug(n["backlog"]),
              };
            }
            return n;
          }),
        },
      };
    }),
  };
}

// ─── Reading ────────────────────────────────────────────────────────────────

/**
 * Parse untrusted input — localStorage contents or an imported file.
 * A version 1 through 8 save is migrated up; anything else is rejected.
 * Never throws.
 *
 * Checks the shape *and* the references inside a world — see validWorld().
 *
 * @param slotId  Id for the slot a version 1 save is migrated into.
 * @param slotName Name for that slot. Player-facing, hence supplied from ui/.
 */
/**
 * Length and lanes move from the route to a corridor of its own (RFC-0033).
 *
 * Every v9 route becomes exactly one corridor with the same two endpoints, the
 * same length and the same lanes, and a route that points at it. That is the
 * whole migration, and it is lossless by construction: a path of one corridor
 * *is* the old road, which is invariant I5 and the reason PR 1 exists on its own
 * (§8).
 *
 * The length is carried over rather than recomputed from the nodes. They agree
 * today — buildRoute() wrote `distance(a, b)` — but a hand-edited save or a node
 * that moved would otherwise have its roads silently re-measured by a
 * migration, and a migration that changes numbers is not one.
 */
function migrateV9(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    ...v,
    version: 10,
    slots: slots.map((s: unknown) => {
      if (!isRecord(s) || !isRecord(s["world"])) return s;
      const world = s["world"];
      const routes = Array.isArray(world["routes"]) ? world["routes"] : [];
      let nextCorridorId = 1;
      const corridors: Record<string, unknown>[] = [];
      const migrated = routes.map((r: unknown) => {
        if (!isRecord(r)) return r;
        const id = nextCorridorId++;
        corridors.push({
          id,
          from: r["from"],
          to: r["to"],
          length: r["length"],
          lanes: r["lanes"],
        });
        const { length: _length, lanes: _lanes, ...rest } = r;
        return { ...rest, corridors: [id] };
      });
      return {
        ...s,
        world: { ...world, corridors, routes: migrated, nextCorridorId },
      };
    }),
  };
}

function migrateV10(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return { ...v, version: 11, slots: slots.map((s: unknown) => isRecord(s) && isRecord(s["world"])
    ? { ...s, world: { ...s["world"], regionCount: 1 } } : s) };
}

function migrateV11(v: Record<string, unknown>): Record<string, unknown> {
  const slots = Array.isArray(v["slots"]) ? v["slots"] : [];
  return {
    ...v,
    version: 12,
    slots: slots.map((slot: unknown) => isRecord(slot) ? { ...slot, kind: "snapshot" } : slot),
  };
}

export function parseSave(raw: string, slotId: string, slotName = "Alter Spielstand"): SaveFile | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(data)) return null;
  if (!validEnvelope(data)) return null;

  let save = data;
  if (save["version"] === 1) save = migrateV1(save, slotId, slotName);
  if (save["version"] === 2) save = migrateV2(save);
  if (save["version"] === 3) save = migrateV3(save);
  if (save["version"] === 4) save = migrateV4(save);
  if (save["version"] === 5) save = migrateV5(save);
  if (save["version"] === 6) save = migrateV6(save);
  if (save["version"] === 7) save = migrateV7(save);
  if (save["version"] === 8) save = migrateV8(save);
  if (save["version"] === 9) save = migrateV9(save);
  if (save["version"] === 10) save = migrateV10(save);
  if (save["version"] === 11) save = migrateV11(save);
  if (save["version"] !== SAVE_VERSION) return null;

  const slots = save["slots"];
  if (!Array.isArray(slots)) return null;
  // One broken slot rejects the whole file: a save quietly missing a world the
  // player remembers is worse than a save that visibly did not load.
  if (!slots.every(validSlot)) return null;

  const active = save["activeSlot"];
  if (active !== null && !isStr(active)) return null;
  if (active !== null && !slots.some((s) => s.id === active)) return null;

  return save as unknown as SaveFile;
}
